import { FirebaseAudio } from "@components/FirebaseAudio";
import { DeleteIcon } from "@components/Icons/Delete";
import { Modal } from "@components/Modal";
import { useTranslation } from "@helpers/useTranslation";
import { deleteObject, ref, uploadBytes } from "firebase/storage";
import { FC, useContext, useState } from "react";
import { FirebaseContext } from "src/helpers/firebase";
import { useEventId } from "src/helpers/useEvent";
import { useToast } from "src/helpers/useToast";

type Props = {
  show: boolean;
  onClose: () => void;
  challengeId: string;
  audioUri?: string;
  onAudioChange: (audioUri: string | undefined) => void;
};

/**
 * Admin upload of an MP3/WAV/M4A sound file guests can play from the info challenge page. Stored at
 * events/{eventId}/challenges/{challengeId}/audio.(mp3|wav|m4a) — files in the challenges folder are
 * skipped by the image-processing trigger (audio extensions are on its ignore list) and excluded
 * from the gallery ZIP download (which only walks the teams folder).
 */
export const AudioModal: FC<Props> = ({ show, onClose, challengeId, audioUri, onAudioChange }) => {
  const { t } = useTranslation("admin");
  const { storage } = useContext(FirebaseContext);
  const eventId = useEventId();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const basePath = `events/${eventId}/challenges/${challengeId}/audio`;

  const handleNewAudio = async (file: File) => {
    const name = file.name.toLowerCase();
    const extension = ["mp3", "wav", "m4a"].find((ext) => name.endsWith(`.${ext}`));
    if (!extension) {
      toast(t("audioInvalidType"), "🙉", "error");
      return;
    }

    setUploading(true);
    try {
      const path = `${basePath}.${extension}`;
      // Replacing an .mp3 with a .wav (or vice versa) would leave the old file behind — remove it
      if (audioUri && audioUri !== path) {
        await deleteObject(ref(storage, audioUri)).catch(() => undefined);
      }
      await uploadBytes(ref(storage, path), file, { customMetadata: { challengeId, eventId } });
      onAudioChange(path);
    } catch (error) {
      console.error("Error uploading audio file:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!audioUri) return;
    setDeleting(true);
    try {
      // The file may already be gone (deleted earlier but the edit was cancelled, leaving the
      // doc pointing at nothing) — clearing the reference must still succeed.
      await deleteObject(ref(storage, audioUri)).catch((error) => {
        if (error?.code !== "storage/object-not-found") throw error;
      });
      onAudioChange(undefined);
    } catch (error) {
      console.error("Error deleting audio file:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal className="z-50" show={show} onCancel={onClose} size="lg">
      <h3 className="mb-2 text-lg font-bold">{t("audio")}</h3>
      <p className="mb-4 text-sm opacity-70">{t("audioDescription")}</p>

      {audioUri ? (
        <div className="flex flex-col items-center">
          <FirebaseAudio key={audioUri} uri={audioUri} className="mb-4 w-full max-w-md" />
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className={`btn-error btn ${deleting ? "loading" : ""}`}
            >
              <DeleteIcon className="mr-2 h-4 w-4" />
              {t("delete")}
            </button>
            <button type="button" onClick={onClose} className="btn">
              {t("close")}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-center">
            <label htmlFor="audio-file-input" className={`btn-outline btn-primary btn ${uploading ? "loading" : ""}`}>
              {t("audioAdd")}
              <input
                id="audio-file-input"
                type="file"
                accept=".mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a"
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleNewAudio(file);
                }}
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={onClose} className="btn">
              {t("close")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};
